/**
 * ScriptsInjector — the single glue that injects the tenant's custom
 * Header/Footer scripts (WordPress-style "Insert Headers and Footers") into
 * the live document.
 *
 *   • Header scripts  → appended to <head> (after existing head content).
 *   • Footer scripts  → appended to the END of <body> (before </body>).
 *
 * The raw strings may each contain MANY independent blocks (<script>, <style>,
 * <meta>, <link>, comments). Because assigning innerHTML does NOT execute
 * injected <script> tags, we parse the markup and re-create each <script> as a
 * fresh element (copying attributes + inline code) so the browser executes it;
 * all other nodes are cloned across as-is.
 *
 * Idempotent: every re-inject first removes the nodes from the previous run
 * (tracked in dedicated wrapper containers), so a settings refresh / edit never
 * duplicates tags. Fail-safe: a malformed snippet is logged, never thrown, so a
 * bad script can never break rendering.
 *
 * Config is read from the SAME runtime settings source as branding/tracking
 * (SettingsProvider → effective_scripts), so it works pre- and post-login and
 * takes effect on the next page load with no redeploy.
 */
import { useEffect } from "react";
import { useSettings } from "@/lib/settings-context";

const HEADER_MARK = "analyzio-header-scripts";
const FOOTER_MARK = "analyzio-footer-scripts";

/** Turn a raw HTML/JS string into an array of live DOM nodes, re-creating
 *  <script> elements so they actually execute. */
function materialize(raw) {
    const nodes = [];
    if (!raw || !raw.trim()) return nodes;
    const tpl = document.createElement("template");
    tpl.innerHTML = raw;
    Array.from(tpl.content.childNodes).forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === "SCRIPT") {
            const s = document.createElement("script");
            // Copy every attribute (src, async, type, data-*, nonce, etc.).
            Array.from(node.attributes).forEach((a) => s.setAttribute(a.name, a.value));
            // Inline code (no src) must be copied as text so it runs.
            if (node.textContent) s.textContent = node.textContent;
            nodes.push(s);
        } else {
            // meta / link / style / comments / text — clone verbatim.
            nodes.push(node.cloneNode(true));
        }
    });
    return nodes;
}

/** Remove any container we previously injected under `markId`. */
function clearPrevious(markId) {
    document.querySelectorAll(`[data-analyzio-scripts="${markId}"]`).forEach((el) => el.remove());
}

function inject(target, raw, markId, position) {
    try {
        clearPrevious(markId);
        const nodes = materialize(raw);
        if (!nodes.length) return;
        // Wrap in a marker container so we can cleanly remove on re-inject.
        // (Scripts still execute — they're moved into the container which is
        //  attached to the live document.)
        const holder = document.createElement("div");
        holder.setAttribute("data-analyzio-scripts", markId);
        holder.style.display = "none";
        // Non-script nodes stay inside the holder; scripts must be appended to
        // the target directly to execute reliably across browsers, but we tag
        // them so cleanup still finds them.
        nodes.forEach((n) => {
            if (n.tagName === "SCRIPT") {
                n.setAttribute("data-analyzio-scripts", markId);
                if (position === "prepend" && target.firstChild) {
                    target.insertBefore(n, target.firstChild);
                } else {
                    target.appendChild(n);
                }
            } else {
                holder.appendChild(n);
            }
        });
        if (holder.childNodes.length) {
            if (position === "prepend" && target.firstChild) {
                target.insertBefore(holder, target.firstChild);
            } else {
                target.appendChild(holder);
            }
        }
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`[scripts] failed to inject ${markId}`, e);
    }
}

export default function ScriptsInjector() {
    // `scripts` is provided by SettingsProvider from the SAME runtime source
    // as branding/tracking: authenticated → /settings effective_scripts;
    // anonymous (pre-login) → /branding/lookup header_scripts/footer_scripts.
    const { scripts } = useSettings();
    const header = scripts?.header_scripts || "";
    const footer = scripts?.footer_scripts || "";

    useEffect(() => {
        inject(document.head, header, HEADER_MARK, "append");
    }, [header]);

    useEffect(() => {
        // Footer → very end of <body>, i.e. just before </body>.
        inject(document.body, footer, FOOTER_MARK, "append");
    }, [footer]);

    return null;
}
